'use client';

import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { useConversations } from '@shared/utils/hooks/useConversations';
import useUser from '@shared/utils/hooks/useUser';
import { Loader2, MessageCircle } from 'lucide-react';
import { useState } from 'react';

import { client } from '../../utils/trpc';
import { useChatWorkspace } from '../pages/chat/ChatWorkspaceContext';

/**
 * Opens a DM conversation with the given user.
 * Accepts either a userId directly, or a username to resolve first.
 * The chat workspace is rendered globally by Main, so this works on any page.
 */
const DmButton = ({
  userId,
  username,
  label = 'Message me on Factiii',
  className
}: {
  userId?: number;
  username?: string;
  label?: string;
  className?: string;
}) => {
  const { currentUser } = useUser();
  const { addConversations } = useConversations();
  const { openChat } = useChatWorkspace();
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!currentUser?.id) return;
    setLoading(true);

    try {
      let targetId = userId;

      // Resolve username to userId if needed
      if (!targetId && username) {
        const searchResult = await unwrapFromResult(
          client.search.users.query({ query: username, spaceIds: [] })
        );
        const match = searchResult.users.find(
          (u) => u.username.toLowerCase() === username.toLowerCase()
        );
        if (!match) return;
        targetId = match.id;
      }

      if (!targetId) return;

      const [, result] = await client.messages.start.mutate({
        participants: [currentUser.id, targetId]
      });
      if (result) {
        addConversations([result]);
        openChat(result.id);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={loading || !currentUser?.id}
      className={
        className ??
        'mt-3 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-emphasis text-white transition-colors hover:bg-primary/90 disabled:opacity-50'
      }
    >
      {loading ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <MessageCircle size={16} />
      )}
      {label}
    </button>
  );
};

export default DmButton;
